import React, { useState } from 'react';
import { PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MAX_BUTTON_LABEL } from '@claimup/shared/constants/email-templates';

/**
 * Insertar un botón **en el cuerpo**, donde esté el cursor.
 *
 * Antes esto era una lista de botones debajo del editor, y siempre salían al
 * final del correo. Dos problemas, y el segundo era el gordo:
 *
 *   1. No se podía decidir **dónde** va. Un aviso de pago quiere el botón justo
 *      después de la frase que lo pide, no al final del todo.
 *   2. **Las vistas previas no lo pintaban**, porque se montan del cuerpo y el
 *      botón no estaba en el cuerpo. Hacías clic, aparecía una fila aquí, y la
 *      previa no cambiaba: afirmaba por omisión que no había botón.
 *
 * Ahora el botón se mete dentro del texto y lo segundo deja de poder pasar: lo
 * que se ve es lo que hay, porque sale del mismo sitio.
 *
 * **Para cambiarlo o quitarlo se borra y se pone otro.** No hay edición en
 * sitio: es lo que falta de esta pieza, y se dice en la pantalla en vez de
 * dejar que alguien lo descubra pinchando.
 */

const Rapido = ({ children, titulo, onClick, deshabilitado }) => (
  <button
    type='button'
    onClick={onClick}
    disabled={deshabilitado}
    title={titulo}
    className='flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40'
  >
    <PlusIcon className='w-3 h-3' />
    {children}
  </button>
);

const BotonesPlantilla = ({ onInsertar, whatsapp, soloLectura = false }) => {
  const [abierto, setAbierto] = useState(false);
  const [label, setLabel] = useState('');
  const [href, setHref] = useState('');

  if (soloLectura) return null;

  const insertarPropio = () => {
    if (!label.trim() || !href.trim()) return;
    onInsertar({ label: label.trim(), href: href.trim() });
    setLabel('');
    setHref('');
    setAbierto(false);
  };

  return (
    <div>
      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='text-xs text-slate-500'>Insertar un botón aquí:</span>

        <Rapido
          titulo='Lleva al enlace de pago del expediente'
          onClick={() => onInsertar({ label: 'Pagar ahora', href: '{{payment_url}}' })}
        >
          Pago
        </Rapido>

        <Rapido
          titulo={
            whatsapp?.available
              ? `Abre WhatsApp con ${whatsapp.phone_number}`
              : 'Esta empresa no tiene ningún número de WhatsApp conectado'
          }
          deshabilitado={!whatsapp?.available}
          onClick={() => onInsertar({ label: 'Escríbenos por WhatsApp', href: whatsapp.href })}
        >
          WhatsApp
        </Rapido>

        <Rapido titulo='Un botón con el texto y el destino que tú pongas' onClick={() => setAbierto(a => !a)}>
          Otro
        </Rapido>

        <span className='text-[11px] text-slate-400'>
          Va donde esté el cursor. Para cambiarlo, bórralo y pon otro.
        </span>
      </div>

      {abierto && (
        <div className='mt-2 flex flex-wrap items-start gap-2'>
          <input
            value={label}
            maxLength={MAX_BUTTON_LABEL}
            onChange={e => setLabel(e.target.value)}
            placeholder='Ver el detalle'
            aria-label='Texto del botón'
            className='w-40 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:border-blue-400 focus:outline-none'
          />
          <input
            value={href}
            onChange={e => setHref(e.target.value)}
            placeholder='{{payment_url}} o https://…'
            aria-label='Destino del botón'
            className='flex-1 min-w-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-mono focus:border-blue-400 focus:outline-none'
          />
          <button
            type='button'
            onClick={insertarPropio}
            disabled={!label.trim() || !href.trim()}
            className='rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40'
          >
            Insertar
          </button>
        </div>
      )}

      {whatsapp && !whatsapp.available ? (
        <p className='text-[11px] text-slate-400 mt-1.5'>
          {whatsapp.reason === 'not_connected'
            ? 'El número de WhatsApp de esta empresa no está conectado, así que ese botón no se puede ofrecer.'
            : 'Esta empresa no tiene número de WhatsApp, así que ese botón no se puede ofrecer.'}
        </p>
      ) : null}

      {whatsapp?.available ? (
        <div className='mt-2 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700'>
          <ExclamationTriangleIcon className='w-3.5 h-3.5 shrink-0 mt-px' />
          <span>
            Si pones el botón de WhatsApp y el deudor escribe desde un móvil que no tenemos, su
            mensaje cae en <strong>WhatsApp sin asignar</strong> y hay que repescarlo desde ahí.
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default BotonesPlantilla;
